import { patchSession, sessionService } from "@/app/providers/services";
import { colors } from "@/theme";
import Icon from "@imminently/decisively-ui/Icon";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { EmptyContent, JsonEditor } from "@/common";
import { useProject, useRelease, useWorkspace } from "@/common/hooks";
import { useNotification } from "@/common/notifications";
import { StatusLabel } from "@/features/tasks/TaskComponents";
import { DecisionGraph, decisionGraphToStoredGraph } from "@imminently/decisively-core";
import { SessionGraphContext, useSessionGraph } from "./sessionGraph";
import Button from "@imminently/decisively-ui/Button";
import Chip from "@imminently/decisively-ui/Chip";
import Detail from "@imminently/decisively-ui/Detail";
import DetailHorizontalList from "@imminently/decisively-ui/DetailHorizontalList";
import Dialog from "@imminently/decisively-ui/Dialog";
import LoadingOverlay from "@imminently/decisively-ui/LoadingOverlay";
import Select from "@imminently/decisively-ui/Select";
import Tabs, { type TabValue } from "@imminently/decisively-ui/Tabs";

import { ParsedGraphContext } from "@/pages/models/release/GraphContext";
import { Breadcrumbs, Typography } from "@mui/material";

import { useErrorReporting } from "@/ErrorReporting";
import { useHasRole } from "@/auth/permissions";
import { crudGetOne } from "@/legacy/redux";
import { useScope } from "@/redux/scope";
import { useDispatch } from "react-redux";
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import { DialogHeader, DialogControls } from "@/ui/dialogs/DialogHeader";
import { DateTime } from "./components";
import { ContactsPanel } from "./components/ContactsPanel";
import { DataPanel } from "./components/DataPanel";
import EventTab from "./components/EventTab";
import { FilesTab } from "./components/FilesTab";
import InputTab from "./components/InputTab";
import { InteractionsPanel } from "./components/InteractionsPanel";
import InvestigationsTab from "./components/InvestigationsTab";
import MappingTab from "./components/MappingTab";
import SessionModalDevPanel from "./components/SessionModalDevPanel";
import TriggersTab from "./components/TriggersTab";
import { VisualisationsPanel } from "./components/VisualisationsPanel";

export interface SessionModalProps {
  sessionId: string;
  versionId?: string;
  releaseId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SessionReportContentProps {
  sessionId: string;
  versionId?: string;
  releaseId?: string;
  fullScreen: boolean;
  allowResize: boolean;
}

const SessionReportContent = ({ sessionId, versionId, releaseId, fullScreen, allowResize }: SessionReportContentProps) => {
  const notify = useNotification();
  const { t } = useTranslation();
  const errorReporting = useErrorReporting();
  const dispatch = useDispatch();
  const { workspace: workspaceId, project: projectId, release: buildRelease } = useScope();
  const workspace = useWorkspace(workspaceId);
  const project = useProject(projectId);
  const canSeeTasks = useHasRole(["task-read", "task-read-all"]);
  const canSeeContacts = useHasRole(["contacts-read-all", "contacts-writer"]);

  const [selectedVersion, setSelectedVersion] = useState<string | null>(versionId || null);
  const [maxVersion, setMaxVersion] = useState<number | null>(null);

  // Fetch the latest session to determine maxVersion only when viewing an explicit historical version.
  const { data: latestSession } = sessionService.useGetOne(sessionId, {
    params: { graph: false },
    enabled: Boolean(selectedVersion) && maxVersion === null,
    refetchOnWindowFocus: false,
  });

  const {
    data: session,
    isError,
    isLoading,
    refetch,
  } = sessionService.useGetOne(selectedVersion ? `${sessionId}-${selectedVersion}` : sessionId, {
    // the saved decision graph rather than the legacy graph built from it
    params: { graph: "dg" },
    refetchOnWindowFocus: false,
  });
  const effectiveReleaseId = session?.release ?? releaseId ?? buildRelease;
  const release = useRelease(effectiveReleaseId);

  useEffect(() => {
    if (effectiveReleaseId) {
      dispatch(crudGetOne("releases", effectiveReleaseId, { refresh: false, onFailure: {} }));
    }
  }, [dispatch, effectiveReleaseId]);

  // Reset state when sessionId or versionId changes (new session)
  useEffect(() => {
    setMaxVersion(null);
    setSelectedVersion(versionId || null);
  }, [sessionId, versionId]);

  // Set maxVersion from the latest session when it loads
  useEffect(() => {
    if (latestSession?.version && maxVersion === null) {
      setMaxVersion(latestSession.version);
    }
  }, [latestSession?.version, maxVersion]);

  useEffect(() => {
    if (!selectedVersion && session?.version && maxVersion === null) {
      setMaxVersion(session.version);
    }
  }, [maxVersion, selectedVersion, session?.version]);

  // Compute the current display value for the Select - always use session.version as fallback
  const currentVersionValue = useMemo(() => {
    if (selectedVersion) {
      return selectedVersion;
    }
    if (session?.version) {
      return String(session.version);
    }
    return null;
  }, [selectedVersion, session?.version]);
  const releaseGraph = useMemo<DecisionGraph>(
    () => (release?.rule_graph ? DecisionGraph.from(release.rule_graph as any) : new DecisionGraph()),
    [release?.rule_graph],
  );
  // the session's saved decision graph, read as is (no legacy graph built, no re-solve on open)
  const [sessionGraph, graphError, graphUnavailable] = useSessionGraph(session, release, releaseGraph);
  const [rawSessionOpen, setRawSessionOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabValue["value"]>("decision");
  const [inputTabSelectInteractionId, setInputTabSelectInteractionId] = useState<string | null>(null);

  const handleGoToInputAndSelectInteraction = useCallback((interactionId: string) => {
    setActiveTab("Input");
    setInputTabSelectInteractionId(interactionId);
  }, []);
  const openRawSessionModal = useCallback(() => {
    setRawSessionOpen(true);
  }, []);

  const openSessionFeedback = useCallback(() => {
    const feedbackSessionId = session?.id || (selectedVersion ? `${sessionId}-${selectedVersion}` : sessionId);
    errorReporting.open({ sessionId: feedbackSessionId });
  }, [errorReporting, selectedVersion, session?.id, sessionId]);

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refetch?.();
    } catch (error) {
      console.error("Failed to refresh session", error);
      notify.error("Failed to refresh session");
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch, isRefreshing, notify]);

  const handleDownloadGraph = useCallback(() => {
    if (!sessionGraph) {
      return;
    }
    try {
      // the legacy saved-graph JSON, made only when asked for
      const graphJson = JSON.stringify(
        session?.decisionGraph ? decisionGraphToStoredGraph(session.decisionGraph) : decisionGraphToStoredGraph(sessionGraph),
        null,
        2,
      );
      const blob = new Blob([graphJson], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `session-graph-${sessionId}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download graph", error);
      notify.error("Failed to download graph");
    }
  }, [sessionGraph, session?.decisionGraph, sessionId, notify]);

  const wrapTabContent = (content: React.ReactNode, needsGraph: boolean, tabValue: TabValue["value"]) => {
    const isActiveAndNeedsGraph = activeTab === tabValue && needsGraph && !sessionGraph;
    if (isActiveAndNeedsGraph && graphUnavailable) {
      // the session has no saved graph and solving it for one failed: say so rather than load forever
      return <EmptyContent messages={[graphUnavailable]} img="" style={{ width: "400px", height: "300px" }} />;
    }
    if (isActiveAndNeedsGraph) {
      return (
        <div className="relative w-full h-full">
          {content}
          <LoadingOverlay message={"Loading decision results..."} />
        </div>
      );
    }
    return content;
  };

  if (graphError) {
    return (
      <EmptyContent
        messages={[graphError]}
        img=""
        style={{ width: "400px", height: "300px" }}
      />
    );
  }

  const lastInteraction = session?.interactions?.find((i: any) => i.id === session.lastInteractionId);

  const tabs: TabValue[] = [
    {
      value: "decision",
      label: "Decision",
      content: wrapTabContent(
        <InvestigationsTab
          sessionId={sessionId}
          sessionVersion={Number(session?.version) || null}
          modelId={project?.id}
          session={session}
          sessionGraph={sessionGraph}
          releaseGraph={releaseGraph}
          ruleGraph={release?.rule_graph}
          releaseRelationships={release?.relationships || []}
          sessionGoal={session?.goal}
          canReadContacts={canSeeContacts}
        />,
        true,
        "decision",
      ),
    },
    canSeeTasks
      ? {
          value: "interactions",
          label: "Tasks",
          content: wrapTabContent(<InteractionsPanel session={session} />, false, "interactions"),
        }
      : undefined,
    canSeeContacts
      ? {
          value: "contacts",
          label: "Contacts",
          content: wrapTabContent(
            <ContactsPanel session={session} release={release} canOpenEntities={Boolean(release?.id) && release?.id === buildRelease} />,
            false,
            "contacts",
          ),
        }
      : undefined,
    {
      value: "data",
      label: "Data",
      content: wrapTabContent(
        <DataPanel
          session={session}
          release={release}
          onGoToInputAndSelectInteraction={handleGoToInputAndSelectInteraction}
        />,
        true,
        "data",
      ),
    },
    {
      value: "Input",
      label: "Interactions",
      content: wrapTabContent(
        <InputTab
          session={session}
          graph={sessionGraph}
          release={release}
          selectInteractionId={inputTabSelectInteractionId}
          onSelectInteractionHandled={() => setInputTabSelectInteractionId(null)}
        />,
        false,
        "Input",
      ),
    },
    {
      value: "triggers",
      label: "Triggers",
      content: wrapTabContent(
        <TriggersTab
          session={session}
          release={release}
        />,
        true,
        "triggers",
      ),
    },
    {
      value: "visuals",
      label: "Visualisations",
      content: wrapTabContent(
        <VisualisationsPanel
          session={session}
          release={release}
        />,
        true,
        "visuals",
      ),
    },
    {
      value: "files",
      label: "Files",
      content: wrapTabContent(
        <FilesTab
          sessionId={sessionId}
          versionId={selectedVersion ?? undefined}
          sessionAttachments={session?.attachments}
          interactionAttachments={lastInteraction?.attachments}
        />,
        false,
        "files",
      ),
    },
    {
      value: "mapping",
      label: "Mapping",
      content: wrapTabContent(
        <MappingTab
          session={session}
          release={release}
          sessionId={sessionId}
          versionId={selectedVersion ?? undefined}
        />,
        false,
        "mapping",
      ),
    },
  ].filter((item): item is TabValue => item !== undefined);

  const eventTab = {
    value: "events",
    label: "Events",
    content: wrapTabContent(<EventTab session={session} />, false, "events"),
  };

  return (
    <>
      <Dialog
        open={rawSessionOpen}
        onOpenChange={setRawSessionOpen}
        intent="cover"
      >
        <Dialog.Content>
          <DialogHeader title="Session" controls={{ allowResize: false }} />
          <Dialog.Body>
            <JsonEditor
              className="w-full min-w-[80vw] min-h-[50vh]"
              value={JSON.stringify(session, null, 2)}
              disabled
              withCopy
            />
          </Dialog.Body>
        </Dialog.Content>
      </Dialog>
      {(() => {
        const headerHeight = 40 + 62 + 58 + 50; // combined size in px of the header, info, status bar and tabs
        const height = fullScreen ? `calc(100vh - ${headerHeight}px)` : `calc(100vh - 4rem - ${headerHeight}px)`;

        if (isLoading && !isRefreshing) {
          return (
            <Dialog.Content className="flex flex-col">
              <Dialog.Header className={"!p-0"}>
                <DialogControls
                  allowResize={allowResize}
                  className={"!py-[0.25rem]"}
                  feedback={false}
                />
              </Dialog.Header>
              <LoadingOverlay message={"Loading session..."} />
            </Dialog.Content>
          );
        }

        if (isError || !session) {
          return (
            <Dialog.Content className="p-0">
              <Dialog.Header>
                <DialogControls allowResize={allowResize} feedback={false} />
              </Dialog.Header>
              <div className="flex items-center justify-center w-full h-full min-h-100">
                <EmptyContent
                  messages={["Failed to load session"]}
                  img=""
                  className="w-100 h-75"
                />
              </div>
            </Dialog.Content>
          );
        }

        return (
          <Dialog.Content className="flex flex-col">
            <SessionModalDevPanel
              session={session}
              release={release}
            />
            <Dialog.Header className={"!p-0"}>
              <DialogControls
                allowResize={allowResize}
                className={"!py-[0.25rem]"}
                feedback={openSessionFeedback}
                menuContent={
                  <>
                    <DropdownMenu.Item onClick={handleDownloadGraph} disabled={!sessionGraph}>
                      <DropdownMenu.Item.Icon><Icon name="download" /></DropdownMenu.Item.Icon>
                      <DropdownMenu.Item.Label>Download graph</DropdownMenu.Item.Label>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item onClick={handleRefresh} disabled={isRefreshing}>
                      <DropdownMenu.Item.Icon><Icon name="refresh" /></DropdownMenu.Item.Icon>
                      <DropdownMenu.Item.Label>Refresh session</DropdownMenu.Item.Label>
                    </DropdownMenu.Item>
                  </>
                }
              />
            </Dialog.Header>
            {isRefreshing && <LoadingOverlay message="Refreshing session..." />}
            <SessionGraphContext.Provider value={sessionGraph}>
            <ParsedGraphContext.Provider value={releaseGraph}>
              <div className="flex justify-between px-4 py-2">
                <Breadcrumbs aria-label="breadcrumb">
                  <span className="flex items-center gap-1 text-black text-sm font-medium leading-4 font-['Nunito']">
                    <Icon
                      name="citizenship"
                      className="w-4 h-4"
                    />
                    <span>{workspace?.name}</span>
                  </span>
                  <span className="flex items-center gap-1 text-black text-sm font-medium leading-4 font-['Nunito']">
                    <Icon
                      name="build"
                      className="w-4 h-4"
                    />
                    <span>{project?.name}</span>
                  </span>
                  <span className="flex items-center gap-1 text-black text-sm font-medium leading-4 font-['Nunito']">
                    <Icon
                      name="documents"
                      className="w-4 h-4"
                    />
                    <span>Release {release?.releaseNo}</span>
                  </span>
                </Breadcrumbs>
              </div>
              <div className="min-w-full flex flex-col flex-1 min-h-0">
                <div className="flex gap-4 py-4 px-4 items-baseline border-t border-border max-h-16 text-muted-foreground">
                  <Typography
                    variant="h3"
                    color="textPrimary"
                  >
                    Session Report
                  </Typography>
                  <Typography
                    onClick={() => {
                      navigator.clipboard.writeText(session.id);
                      notify.success("Session ID copied to clipboard");
                    }}
                    variant="h6"
                    color="inherit"
                  >
                    #{session.id}
                  </Typography>
                  <DateTime
                    hideIcon
                    value={session.lastModified}
                    prefix="Last update:"
                  />
                  <Button
                    icon
                    onClick={openRawSessionModal}
                    className="ml-auto"
                    label="Open source"
                  >
                    <Icon name="code" />
                  </Button>
                </div>

                <DetailHorizontalList className="border-t border-b border-border">
                  <DetailHorizontalList.Item>
                    <DetailHorizontalList.Icon>
                      <Icon name="record" />
                    </DetailHorizontalList.Icon>
                    <DetailHorizontalList.Title>Status:</DetailHorizontalList.Title>
                    <DetailHorizontalList.Content>
                      <StatusLabel status={session.status} />
                    </DetailHorizontalList.Content>
                  </DetailHorizontalList.Item>
                  <DetailHorizontalList.Item>
                    <DetailHorizontalList.Title>Version:</DetailHorizontalList.Title>
                    <DetailHorizontalList.Content>
                      {session.version && maxVersion && currentVersionValue ? (
                        <Select
                          value={currentVersionValue}
                          options={Array.from({ length: maxVersion }, (_, i) => maxVersion - i).map((v) => ({
                            value: String(v),
                            label: String(v),
                          }))}
                          onValueChange={(v) => v != null && setSelectedVersion(v)}
                          searchable={false}
                          allowArbitrary={true}
                          className="min-w-12"
                        />
                      ) : (
                        <Chip>{session.version}</Chip>
                      )}
                    </DetailHorizontalList.Content>
                  </DetailHorizontalList.Item>
                  <DetailHorizontalList.Item>
                    <DetailHorizontalList.Icon>
                      <Icon name="interactions" />
                    </DetailHorizontalList.Icon>
                    <DetailHorizontalList.Title>Interactions:</DetailHorizontalList.Title>
                    <DetailHorizontalList.Content>
                      <Chip>{session.totalInteractions}</Chip>
                    </DetailHorizontalList.Content>
                  </DetailHorizontalList.Item>
                  {session.lastInteractionId && session.interactions && session.interactions.length > 0 && (
                    <DetailHorizontalList.Item>
                      <DetailHorizontalList.Title>Latest interaction:</DetailHorizontalList.Title>
                      <DetailHorizontalList.Content>
                        <Chip>{lastInteraction?.mode ?? "Invalid last interaction"}</Chip>
                      </DetailHorizontalList.Content>
                    </DetailHorizontalList.Item>
                  )}
                </DetailHorizontalList>

                <Tabs
                  value={activeTab}
                  onValueChange={(v) => setActiveTab(v as TabValue["value"])}
                  className="flex flex-col flex-1 overflow-hidden min-h-0"
                >
                  <Tabs.List className="flex items-center px-4 pt-4 border-b border-border">
                    {[...tabs, eventTab].map((tab) => (
                      <Tabs.Trigger
                        key={`tab-${tab.value}`}
                        value={tab.value}
                      >
                        {tab.label}
                      </Tabs.Trigger>
                    ))}
                  </Tabs.List>
                  {[...tabs, eventTab].map((tab) => (
                    <Tabs.Content
                      key={`content-${tab.value}`}
                      value={tab.value}
                      className="flex flex-1 flex-col p-0 outline-none relative overflow-auto min-h-0 data-[state=active]:flex"
                      style={{ maxHeight: height, height }}
                    >
                      {tab.content}
                    </Tabs.Content>
                  ))}
                </Tabs>
              </div>
            </ParsedGraphContext.Provider>
            </SessionGraphContext.Provider>
          </Dialog.Content>
        );
      })()}
    </>
  );
};

export const SessionModal = ({ sessionId, versionId, releaseId, open, onOpenChange }: SessionModalProps) => (
  <Dialog
    open={open}
    onOpenChange={onOpenChange}
    defaultFullScreen={false}
    intent="cover"
  >
    {({ fullScreen }) => (
      <SessionReportContent
        sessionId={sessionId}
        versionId={versionId}
        releaseId={releaseId}
        fullScreen={fullScreen}
        allowResize={true}
      />
    )}
  </Dialog>
);
